import { Select } from '../../components/ui/form';
import { brand } from '../../config/brand';

/**
 * How a record was made, as the artist states it.
 *
 * ## One definition, four places
 *
 * The upload form, the editor, the track page and the side drawer all have to
 * say the same thing, and the wording is the feature. A picker offering three
 * words without definitions collects opinions rather than facts, and a label
 * that means something different on the page from what it meant on the form is
 * worse than no label.
 *
 * ## The middle option is about authorship, not tools
 *
 * This is the whole design problem. Almost every modern record touches
 * something AI-adjacent: stem separation, pitch correction, algorithmic
 * mastering, drum replacement. A middle option that caught those would be true
 * of nearly everything and would tell a listener nothing, so the line is who
 * authored what you hear rather than what was open on the desk. `CAVEAT` says
 * so on the form, because honest people over-declare when a question is vague
 * and the label dies of it.
 *
 * ## Declared, never verified
 *
 * Nothing can check any of this, so nothing here implies it was checked. The
 * page says the artist stated it. That is the only honest offer a platform in
 * this position can make, and it is a good deal more than one that says nothing.
 */
export type Provenance = 'performed' | 'ai_assisted' | 'ai_generated';

export const PROVENANCE_OPTIONS: { value: Provenance; label: string; hint: string }[] = [
  {
    value: 'performed',
    label: 'Performed',
    hint: 'written and played by people',
  },
  {
    value: 'ai_assisted',
    label: 'AI-assisted',
    hint: 'AI made part of what you hear',
  },
  {
    value: 'ai_generated',
    label: 'AI-generated',
    hint: 'it came from a prompt',
  },
];

/**
 * The sentence that stops the middle option swallowing everything.
 *
 * Shown under the field rather than inside a hint, because it is the one thing
 * somebody needs to read before answering and a hint on one option only reaches
 * whoever hovers that option.
 */
export const PROVENANCE_CAVEAT =
  'Mastering, pitch correction and stem separation are tools, not authorship. ' +
  'They do not make a track AI-assisted.';

const LABEL: Record<Provenance, string> = {
  performed: 'Performed',
  ai_assisted: 'AI-assisted',
  ai_generated: 'AI-generated',
};

/** The fuller form, for the one place with room to say it properly. */
const MEANING: Record<Provenance, string> = {
  performed: 'Written and played by people.',
  ai_assisted: 'AI made part of what you hear: a vocal, an instrument, the lyrics.',
  ai_generated: 'This recording came from a prompt.',
};

export function provenanceLabel(value: Provenance | null): string {
  return value === null ? 'Not stated' : LABEL[value];
}

/**
 * The line on a track page or in the drawer.
 *
 * ## "Not stated" is drawn, not skipped
 *
 * Every track uploaded before this existed has no answer, and leaving those
 * blank would let absence read as `Performed` - which is the one reading that
 * puts a claim in somebody's mouth on the subject where it matters most. So the
 * empty case says it is empty, and says nobody was asked.
 */
export function ProvenanceNote({ value }: { value: Provenance | null }) {
  return (
    <div className="flex flex-col gap-1" data-provenance={value ?? 'not-stated'}>
      <span className="meta-caps text-faint">How it was made</span>

      <span className="text-[15px] text-ink">{provenanceLabel(value)}</span>

      <p className="text-xs leading-relaxed text-faint">
        {value === null
          ? `Uploaded before ${brand.name} asked. The artist has not stated how this was made.`
          : `${MEANING[value]} The artist's own statement, which ${brand.name} does not verify.`}
      </p>
    </div>
  );
}

/**
 * The compact form, for a dense list.
 *
 * Drawn only for the two AI answers, and that asymmetry is deliberate rather
 * than lazy: a chip on every row of a queue would be noise, and the two worth a
 * glance are the ones a listener may want to know before the track starts. The
 * absence of a chip means performed *or* not stated, which is imprecise in a
 * list and exact one tap away on the page.
 */
export function ProvenanceChip({ value, className = '' }: { value: Provenance | null; className?: string }) {
  if (value === null || value === 'performed') return null;

  const short = value === 'ai_generated' ? 'AI' : 'AI+';

  return (
    <span
      role="img"
      aria-label={LABEL[value]}
      title={`${LABEL[value]}: ${MEANING[value]}`}
      data-provenance-chip={value}
      className={`inline-flex h-[15px] shrink-0 items-center rounded-[3px] bg-chip px-1 text-[9px] font-bold leading-none text-chip-ink ${className}`}
    >
      <span aria-hidden>{short}</span>
    </span>
  );
}

/**
 * The field, wherever it is asked.
 *
 * ## Its own row, never a grid cell
 *
 * The caveat below it runs to three lines, and in a two-column grid that left a
 * hole under whatever sat beside it and pushed the next field onto a row of its
 * own. Moving it out fixed the layout, but the better reason is what the two
 * groups are: the grid holds *metadata* - genre, visibility, tempo, key - and
 * this is a *declaration*, like the rights box and the explicit toggle, which
 * already get the full width to explain themselves in.
 *
 * The control itself is held to a readable width. A select stretched across a
 * form is a target the width of the page for a choice of three.
 */
export function ProvenanceField({
  id,
  value,
  onChange,
  disabled = false,
  placeholder = 'Choose one',
}: {
  id: string;
  value: Provenance | '';
  onChange: (next: Provenance) => void;
  disabled?: boolean;
  /** "Not stated" where an existing track may never have been asked. */
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-soft">
        How it was made
      </label>

      <div className="sm:max-w-[18rem]">
        <Select
          id={id}
          value={value}
          disabled={disabled}
          data-provenance-picker
          placeholder={placeholder}
          options={PROVENANCE_OPTIONS}
          onChange={(next) => onChange(next as Provenance)}
        />
      </div>

      {/*
        Under the field rather than as a hint on one option, because it is the
        sentence that decides whether the middle answer means anything and a
        hint only reaches whoever reads that one line.
      */}
      <p className="max-w-xl text-xs leading-relaxed text-faint">{PROVENANCE_CAVEAT}</p>
    </div>
  );
}
